import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../index.css";
import "../../App.css";
import {
  Row,
  Col,
  Input,
  Typography,
  Button,
  Space,
  DatePicker,
  Tag,
  Checkbox,
  Spin,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import {
  handleGoToNextStep,
  handleUpdateErrorsOfOrderCreamForm,
  handleUpdateMedicalHistory,
  handleGoBack,
  saveStep5Api,
  getOrderByTransactionIdApi,
} from "../../redux-toolkit/slices/orderCreamSlice";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { useSelector } from "react-redux";
import ErrorList from "../Items/ErrorsList";
import dayjs from "dayjs";

const medicalHistoryOptions: string[] = [
  "Arthritis",
  "Diabetes",
  "Heart disease",
  "Liver disease",
  "Kidney disease",
  "Other",
];

const OcMedicalHistory = () => {
  const dispatch = useAppDispatch();
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const { isError, errors, medicalHistory, transactionId, currentStep, isLoading } = orderCreamForm;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const [selectedMedicalHistory, setSelectedMedicalHistory] = useState<
    string[]
  >([]);
  const [entries, setEntries] = useState([
    { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
  ]);
  // Fetch order data when component mounts
  useEffect(() => {
    const fetchOrder = async () => {
      if (transactionId) {
        setIsLoadingOrder(true);
        try {
          const orderData = await dispatch(
            getOrderByTransactionIdApi(transactionId)
          ).unwrap();
          // The Redux reducer will automatically populate the state with order data
        } catch (error) {
          console.error("Error loading order:", error);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };
    
    fetchOrder();
  }, [transactionId, dispatch]);

  useEffect(() => {
    setSelectedMedicalHistory(medicalHistory.medicalHistory);
    setEntries(medicalHistory.others);
  }, [medicalHistory]);
  const handleMedicalHistoryChange = (option: string) => {
    dispatch(
      handleUpdateErrorsOfOrderCreamForm({ isError: false, errors: [] })
    );

    if (selectedMedicalHistory.includes(option)) {
      setSelectedMedicalHistory(
        selectedMedicalHistory.filter((item) => item !== option)
      );
    } else {
      setSelectedMedicalHistory([...selectedMedicalHistory, option]);
    }
  };

  const handleAddEntry = () => {
    dispatch(
      handleUpdateErrorsOfOrderCreamForm({ isError: false, errors: [] })
    );

    setEntries([
      ...entries,
      { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
    ]);
  };

  const handleRemoveEntry = (id: number) => {
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const handleEntryChange = (id: number, field: string, value: string) => {
    dispatch(
      handleUpdateErrorsOfOrderCreamForm({ isError: false, errors: [] })
    );
    setEntries(
      entries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };
  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };

  const isOtherSelected = selectedMedicalHistory.includes("Other");
  const handleSubmit = async () => {
    if (selectedMedicalHistory.length > 0) {
      if (isOtherSelected) {
        if (entries.length < 1) {
          const isError = true;
          const errors = ["Please complete your response"];
          return dispatch(
            handleUpdateErrorsOfOrderCreamForm({ isError, errors })
          );
        }
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (!entry.date || !entry.name) {
            const errors = ["Please complete your response"];
            return dispatch(
              handleUpdateErrorsOfOrderCreamForm({ isError: true, errors })
            );
          }
        }
      }
      const medicalHistoryData = {
        others: entries,
        medicalHistory: selectedMedicalHistory,
      };

      dispatch(
        handleUpdateMedicalHistory(medicalHistoryData)
      );

      if (!transactionId) {
        return dispatch(
          handleUpdateErrorsOfOrderCreamForm({
            isError: true,
            errors: ["Transaction ID is missing. Please refresh the page."],
          })
        );
      }

      try {
        // Sanitize medicalHistory to remove _id fields
        const sanitizedMedicalHistory = {
          medicalHistory: selectedMedicalHistory,
          others: entries.map((entry: any) => {
            const { _id, ...rest } = entry;
            return rest;
          }),
        };

        await dispatch(
          saveStep5Api({
            transactionId,
            medicalHistory: sanitizedMedicalHistory,
          })
        ).unwrap();

        // Update URL with new step
        const newParams = new URLSearchParams(searchParams);
        newParams.set("step", "6");
        setSearchParams(newParams, { replace: true });

        dispatch(handleGoToNextStep());
      } catch (error) {
        dispatch(
          handleUpdateErrorsOfOrderCreamForm({
            isError: true,
            errors: ["Failed to save step. Please try again."],
          })
        );
      }
      return;
    }

    const isError = true;
    const errors = ["Please Choose at least one for your medical history"];

    return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
  };

  return (
    <div className="medical-history-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Medical History Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>Do you have any of the following conditions? (Select all that apply) *</strong>
          </label>
          <div className="flex flex-col gap-2">
            {medicalHistoryOptions.map((option) => (
              <label key={option} className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="twinge-checkbox"
                  checked={selectedMedicalHistory.includes(option)}
                  onChange={() => handleMedicalHistoryChange(option)}
                />
                <span className="body1">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Dynamic Diagnosis Entries */}
        {isOtherSelected &&
          entries.map((entry, index) => (
            <div key={entry.id} className="flex flex-col gap-2">
              <label className="h6 text-theme-text-secondary">
                <strong>{`Diagnosis #${index + 1}`}</strong>
              </label>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Input
                    placeholder="Diagnosis"
                    value={entry.name}
                    onChange={(e) =>
                      handleEntryChange(entry.id, "name", e.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="flex-1">
                  <DatePicker
                    value={
                      entry.date ? dayjs(entry.date) : null
                    }
                    picker="month"
                    placeholder="When?"
                    onChange={(date, dateString) => {
                      if (date) {
                        handleEntryChange(
                          entry.id,
                          "date",
                          date.toISOString()
                        );
                      }
                    }}
                    format="MM/YYYY"
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    shape="circle"
                    icon={<PlusOutlined />}
                    onClick={handleAddEntry}
                  />
                  {index > 0 && (
                    <Button
                      shape="circle"
                      icon={<MinusOutlined />}
                      onClick={() => handleRemoveEntry(entry.id)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        {/* Error Display */}
        {isError === true && (
          <div className="mt-6">
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center gap-4" style={{ marginTop: "40px" }}>
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{ color: 'var(--primary-500)', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: 'relative', minWidth: '120px' }}
          >
            {isLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcMedicalHistory;
